/* eslint-disable formatjs/no-literal-string-in-jsx */
import React from 'react'
import type { useAnimationControls } from 'motion/react'

import { BulbIcon } from '@slite/icons'

import type { DemoAgentHandle } from './DemoAgent'
import { DiffCard } from './DemoAgent/DiffCard'
import type { DemoKnowledgeBaseHandle } from './DemoKnowledgeBase'
import type { Scenario, ScenarioDiff } from './scenarios/types'

type AnimationControls = ReturnType<typeof useAnimationControls>

// ─── Pacing (ms) ────────────────────────────────────────────────────────

const COMPOSER_DWELL_BEFORE_START = 1350
const SIGNAL_SCAN_MS = 1050
const ROUTINE_SCAN_MS = 2850
const SOURCE_SIGNAL_HOLD = 1350
const FINDING_REVEAL_DELAY = 1050
const FINDING_HOLD = 3300
const DOC_OUTDATED_HOLD = 750
const DIFF_HOLD = 2700
const CURSOR_REVIEW_PAUSE = 800
const CURSOR_PRESS_DIP = 225
const POST_PRESS_DELAY = 300
const POST_APPLY_DELAY = 1400
export const FINAL_HOLD_MS = 1500

// ─── Cursor motion (seconds) ────────────────────────────────────────────

const CURSOR_ARC_IN_S = 1.1
const CURSOR_MOVE_TO_APPLY_S = 0.9
const CURSOR_DIP_S = 0.1
const CURSOR_RELEASE_S = 0.12
const CURSOR_EXIT_S = 0.4

const DIFF_CARD_RESOLVE_MS = 450

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

type StoryCtx = {
  agent: DemoAgentHandle | null
  kb: DemoKnowledgeBaseHandle | null
  cursor: AnimationControls
  stage: HTMLDivElement | null
  cancelled: () => boolean
}

const wait = (ctx: StoryCtx, ms: number) =>
  sleep(ms).then(() => !ctx.cancelled())

// ─── runApply: reviewer cursor → click Approve ──────────────────────────

async function runApply(ctx: StoryCtx, diff: ScenarioDiff): Promise<void> {
  const wrap = ctx.stage
  if (!wrap) return

  const wrapRect = wrap.getBoundingClientRect()
  const applyEl = wrap.querySelector<HTMLElement>('[data-agent-apply]')
  const diffEl = wrap.querySelector<HTMLElement>('[data-doc-diff]')
  let reviewX = wrapRect.width * 0.5
  let reviewY = wrapRect.height * 0.4
  if (diffEl) {
    const r = diffEl.getBoundingClientRect()
    reviewX = r.left - wrapRect.left + r.width / 2
    reviewY = r.top - wrapRect.top + r.height / 2
  }
  const startX = wrapRect.width * 0.5
  const startY = wrapRect.height * 0.9
  let applyX = wrapRect.width * 0.62
  let applyY = wrapRect.height * 1.05
  if (applyEl) {
    const r = applyEl.getBoundingClientRect()
    applyX = r.left - wrapRect.left + r.width / 2
    applyY = r.top - wrapRect.top + r.height / 2
  }

  ctx.cursor.set({ x: startX, y: startY, opacity: 0, scale: 0.5 })
  await ctx.cursor.start({
    x: [startX, wrapRect.width * 0.28, reviewX],
    y: [startY, reviewY - 24, reviewY],
    opacity: [0, 1, 1],
    scale: [0.5, 1, 1],
    transition: {
      duration: CURSOR_ARC_IN_S,
      ease: 'easeOut',
      times: [0, 0.55, 1],
    },
  })
  if (!(await wait(ctx, CURSOR_REVIEW_PAUSE))) return

  await ctx.cursor.start({
    x: [reviewX, (reviewX + applyX) / 2 + 40, applyX],
    y: [reviewY, (reviewY + applyY) / 2, applyY],
    transition: {
      duration: CURSOR_MOVE_TO_APPLY_S,
      ease: 'easeInOut',
      times: [0, 0.5, 1],
    },
  })
  if (ctx.cancelled()) return

  if (applyEl) applyEl.style.transform = 'scale(0.95)'
  await ctx.cursor.start({
    scale: 0.85,
    transition: { duration: CURSOR_DIP_S },
  })
  await sleep(CURSOR_PRESS_DIP)
  if (applyEl) applyEl.style.transform = 'scale(1)'
  await ctx.cursor.start({
    scale: 1,
    transition: { duration: CURSOR_RELEASE_S },
  })

  ctx.agent?.hide()
  await sleep(POST_PRESS_DELAY)
  ctx.kb?.updateDoc(diff.docId, diff.appliedContent)
  ctx.kb?.changeLabel(diff.docId, 'verified')
  await sleep(POST_APPLY_DELAY)

  await ctx.cursor.start({
    opacity: 0,
    scale: 0.6,
    transition: { duration: CURSOR_EXIT_S },
  })
}

export const scenarioDurationMs = (): number =>
  COMPOSER_DWELL_BEFORE_START +
  SIGNAL_SCAN_MS +
  ROUTINE_SCAN_MS +
  SOURCE_SIGNAL_HOLD +
  FINDING_REVEAL_DELAY +
  FINDING_HOLD +
  DOC_OUTDATED_HOLD +
  DIFF_CARD_RESOLVE_MS +
  DIFF_HOLD +
  CURSOR_REVIEW_PAUSE +
  CURSOR_PRESS_DIP +
  POST_PRESS_DELAY +
  POST_APPLY_DELAY +
  FINAL_HOLD_MS +
  (CURSOR_ARC_IN_S +
    CURSOR_MOVE_TO_APPLY_S +
    CURSOR_DIP_S +
    CURSOR_RELEASE_S +
    CURSOR_EXIT_S) *
    1000

// ─── runScenario: full 6-beat story for one scenario ────────────────────

export async function runScenario(s: Scenario, ctx: StoryCtx): Promise<void> {
  ctx.kb?.navigate(s.initialDocId)
  ctx.agent?.show()
  if (!(await wait(ctx, COMPOSER_DWELL_BEFORE_START))) return

  // beat 1 (signal) + beat 2 (search): both live in one plan group.
  await (ctx.agent?.pushBlock({
    type: 'text',
    text: s.signal.label,
    animation: 'tool',
    duration: SIGNAL_SCAN_MS,
    groupId: s.id,
    icon: s.signal.icon,
  }) ?? Promise.resolve())
  if (ctx.cancelled()) return

  await (ctx.agent?.pushBlock({
    type: 'text',
    text: s.routine.label,
    animation: 'tool',
    duration: ROUTINE_SCAN_MS,
    groupId: s.id,
  }) ?? Promise.resolve())
  if (ctx.cancelled()) return

  await (ctx.agent?.pushBlock({
    type: 'text',
    text: s.sourceSignal.label,
    animation: 'stream',
    icon: s.sourceSignal.icon,
    groupId: s.id,
  }) ?? Promise.resolve())
  if (ctx.cancelled()) return
  if (!(await wait(ctx, SOURCE_SIGNAL_HOLD))) return
  if (!(await wait(ctx, FINDING_REVEAL_DELAY))) return

  // beat 3 (find): state the issue before switching the doc into diff mode.
  ctx.agent?.pushBlock({
    type: 'text',
    text: s.finding,
    animation: 'stream',
    icon: <BulbIcon size={14} style={{ color: 'var(--saturn-orange)' }} />,
  })
  if (!(await wait(ctx, FINDING_HOLD))) return

  // beat 4 (diff): KB flips outdated + navigates.
  ctx.kb?.updateDoc(s.diff.docId, s.diff.outdatedContent)
  ctx.kb?.changeLabel(s.diff.docId, 'outdated')
  ctx.kb?.navigate(s.diff.docId)
  if (!(await wait(ctx, DOC_OUTDATED_HOLD))) return

  // beats 4+5 combined: diff card + routing note.
  ctx.agent?.pushBlock({
    type: 'node',
    title: s.routeNote,
    animation: 'stream',
    duration: DIFF_CARD_RESOLVE_MS,
    node: <DiffCard diff={s.diff.pill} />,
  })
  if (!(await wait(ctx, DIFF_HOLD))) return

  // beat 6: reviewer cursor → click Approve.
  await runApply(ctx, s.diff)
}
